import { useCallback, useEffect, useState } from 'react'
import {
  addPersonaNote,
  assignPersona,
  createPersona,
  deletePersona,
  deletePersonaResearch,
  getOutreachJob,
  getPersona,
  patchPersona,
  startFindPeople,
  startPersonaResearch,
  PERSONA_MESSAGING_FIELDS,
  PERSONA_PROFILE_FIELDS,
  type OutreachJob,
  type PersonaFieldKey,
  type PersonaView,
  type RemoteProspect,
  type RemoteProspectCampaign,
  type ResearchView
} from '../../lib/adminClient'
import { useAuth } from '../../lib/auth'
import { usdFine } from '../../lib/format'
import { Card, Pill, inputCls, shortUrl, when } from './ui'

// Personas: who we are trying to reach, what we know about their market (each
// finding with the pages it rests on), how we write to them, and finding more
// people like them. AI research and finding run on Plexii's own Anthropic key
// and cost money; the page says what each run cost.

type AiInfo = { model: string; available: boolean; spentUsd: number; findMax: number }

export function Personas({
  personas,
  ai,
  people,
  onChanged,
  onOpenPerson
}: {
  personas: PersonaView[]
  ai: AiInfo | null
  people: RemoteProspect[]
  onChanged: () => void
  onOpenPerson: (id: string) => void
}): JSX.Element {
  const session = useAuth()
  const canWrite = session?.role === 'owner' || session?.role === 'admin'
  const [selected, setSelected] = useState<string | null>(personas[0]?.id ?? null)
  const [newName, setNewName] = useState('')
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!selected && personas[0]) setSelected(personas[0].id)
  }, [personas, selected])

  async function create(): Promise<void> {
    setErr(null)
    const r = await createPersona({ name: newName.trim() })
    if (!r.ok) return setErr(r.error)
    setNewName('')
    onChanged()
    setSelected(r.data.persona.id)
  }

  // The segments the research already sorted people into, not yet a persona.
  const segments = [...new Set(people.map((p) => p.segment).filter((s): s is string => !!s))].filter((s) => !personas.some((x) => x.name.toLowerCase() === s.toLowerCase()))

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[18rem_1fr] gap-4 items-start">
      <div className="glass-card rounded-xl p-3 space-y-1" data-testid="personas-list">
        {personas.map((x) => (
          <button
            key={x.id}
            onClick={() => setSelected(x.id)}
            className={`w-full text-left rounded-md px-3 py-2 ${selected === x.id ? 'bg-accent/10' : 'hover:bg-white/[0.03]'}`}
            data-testid={`persona-row-${x.id}`}
          >
            <div className="text-sm font-medium">{x.name}</div>
            <div className="text-[11px] text-slate-500">
              {x.prospects} people · {x.campaigns} campaigns · {x.research} research notes
            </div>
          </button>
        ))}
        {personas.length === 0 && <div className="text-sm text-slate-500 px-3 py-2">No personas yet.</div>}
        {canWrite && (
          <div className="pt-2 mt-2 border-t border-white/5 space-y-2">
            <input className={inputCls} placeholder="New persona, e.g. Agency owners" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="persona-new-name" />
            <button className="btn-primary w-full justify-center" style={{ display: 'flex' }} disabled={!newName.trim()} onClick={() => void create()} data-testid="persona-create">
              Add persona
            </button>
            {segments.length > 0 && (
              <div className="text-[11px] text-slate-500">
                From the research:{' '}
                {segments.map((s, i) => (
                  <button key={s} className="text-accent hover:underline" onClick={() => setNewName(s)}>
                    {s}
                    {i < segments.length - 1 ? ', ' : ''}
                  </button>
                ))}
              </div>
            )}
            {err && <div className="text-xs text-red-300">{err}</div>}
          </div>
        )}
      </div>
      {selected ? (
        <PersonaDetail key={selected} id={selected} ai={ai} people={people} canWrite={canWrite} onChanged={onChanged} onDeleted={() => { setSelected(null); onChanged() }} onOpenPerson={onOpenPerson} />
      ) : (
        <div className="glass-card rounded-xl p-8 text-sm text-slate-400">Add a persona to describe who you want to reach.</div>
      )}
    </div>
  )
}

function PersonaDetail({
  id,
  ai,
  people,
  canWrite,
  onChanged,
  onDeleted,
  onOpenPerson
}: {
  id: string
  ai: AiInfo | null
  people: RemoteProspect[]
  canWrite: boolean
  onChanged: () => void
  onDeleted: () => void
  onOpenPerson: (id: string) => void
}): JSX.Element {
  const [persona, setPersona] = useState<(PersonaView & { researchEntries: ResearchView[] }) | null>(null)
  const [jobs, setJobs] = useState<OutreachJob[]>([])
  const [campaigns, setCampaigns] = useState<RemoteProspectCampaign[]>([])
  const [draft, setDraft] = useState<Partial<Record<PersonaFieldKey | 'name', string>>>({})
  const [err, setErr] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    const r = await getPersona(id)
    if (!r.ok) return setErr(r.error)
    setPersona(r.data.persona)
    setJobs(r.data.jobs)
    setCampaigns(r.data.campaigns)
  }, [id])
  useEffect(() => void load(), [load])

  // Watch running jobs until they finish.
  useEffect(() => {
    const running = jobs.filter((j) => j.status === 'running')
    if (!running.length) return
    const t = setInterval(async () => {
      let finished = false
      for (const j of running) {
        const r = await getOutreachJob(j.id)
        if (r.ok && r.data.job.status !== 'running') finished = true
      }
      if (finished) {
        await load()
        onChanged()
      }
    }, 4000)
    return () => clearInterval(t)
  }, [jobs, load, onChanged])

  if (err && !persona) return <Card><div className="text-sm text-red-300">{err}</div></Card>
  if (!persona) return <Card><div className="text-sm text-slate-400">Loading…</div></Card>

  const value = (k: PersonaFieldKey | 'name'): string => draft[k] ?? ((persona as unknown as Record<string, string | null>)[k] ?? '')
  const dirty = Object.keys(draft).length > 0
  const linked = people.filter((p) => p.personaId === persona.id)
  const sameSegment = people.filter((p) => !p.personaId && (p.segment ?? '').toLowerCase() === persona.name.toLowerCase())

  async function save(): Promise<void> {
    setErr(null)
    const r = await patchPersona(id, draft)
    if (!r.ok) return setErr(r.error)
    setDraft({})
    setNotice('Saved.')
    await load()
    onChanged()
  }
  async function linkSegment(): Promise<void> {
    const r = await assignPersona(id, sameSegment.map((p) => p.id))
    if (!r.ok) return setErr(r.error)
    setNotice(`Linked ${r.data.linked} people.`)
    await load()
    onChanged()
  }
  async function remove(): Promise<void> {
    const r = await deletePersona(id)
    if (!r.ok) return setErr(r.error)
    onDeleted()
  }

  const field = (k: PersonaFieldKey, label: string): JSX.Element => (
    <label key={k} className="block">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <textarea className={inputCls} rows={2} value={value(k)} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`persona-field-${k}`} />
    </label>
  )

  return (
    <div className="space-y-4 min-w-0" data-testid="persona-detail">
      <div className="glass-card rounded-xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <input className="bg-transparent text-xl font-semibold focus:outline-none border-b border-transparent focus:border-accent flex-1 min-w-0" value={value('name')} readOnly={!canWrite} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="persona-name" />
          <div className="flex gap-2 items-center">
            <Pill>{linked.length} people</Pill>
            <Pill>{campaigns.length} campaigns</Pill>
          </div>
        </div>
        {sameSegment.length > 0 && canWrite && (
          <div className="mt-3 text-sm text-slate-300">
            {sameSegment.length} people were researched as “{persona.name}” but are not linked yet.{' '}
            <button className="text-accent hover:underline" onClick={() => void linkSegment()} data-testid="persona-link-segment">
              Link them
            </button>
          </div>
        )}
        {notice && <div className="text-xs text-emerald-300 mt-2">{notice}</div>}
        {err && <div className="text-xs text-red-300 mt-2">{err}</div>}
      </div>

      <Card title="Who they are" testId="persona-profile">
        <div className="grid md:grid-cols-2 gap-3">{PERSONA_PROFILE_FIELDS.map(([k, l]) => field(k, l))}</div>
      </Card>

      <Card title="How we write to them" testId="persona-messaging">
        <div className="text-xs text-slate-400 mb-3">Rules for this market, on top of the general guide (Signatures &amp; guide tab). Each message is still written for one person.</div>
        <div className="grid md:grid-cols-2 gap-3">{PERSONA_MESSAGING_FIELDS.map(([k, l]) => field(k, l))}</div>
      </Card>

      {canWrite && dirty && (
        <div className="flex gap-2 sticky bottom-4">
          <button className="btn-primary" onClick={() => void save()} data-testid="persona-save">
            Save persona
          </button>
          <button className="btn-ghost" onClick={() => setDraft({})}>
            Discard changes
          </button>
        </div>
      )}

      <Research persona={persona} jobs={jobs} ai={ai} canWrite={canWrite} reload={load} />
      <FindPeople persona={persona} jobs={jobs} ai={ai} canWrite={canWrite} value={value('findCriteria')} onCriteria={(v) => setDraft({ ...draft, findCriteria: v })} reload={load} onOpenPerson={onOpenPerson} people={people} />

      <Card title={`People (${linked.length})`} testId="persona-people">
        {linked.length === 0 ? (
          <div className="text-sm text-slate-500">Nobody linked yet. Set the persona on a person, or find people above.</div>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-1">
            {linked.map((p) => (
              <li key={p.id}>
                <button className="text-sm text-left hover:text-accent" onClick={() => onOpenPerson(p.id)}>
                  {p.name}
                  <span className="text-slate-500"> · {p.plan.title}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {canWrite && (
        <div className="text-right">
          <button className="text-xs text-slate-500 hover:text-red-300" onClick={() => void remove()} data-testid="persona-delete">
            Delete this persona (people and campaigns stay; only the link goes)
          </button>
        </div>
      )}
    </div>
  )
}

function JobLine({ job }: { job: OutreachJob }): JSX.Element {
  return (
    <div className="text-xs text-slate-400" data-testid={`job-${job.id}`}>
      {job.kind === 'research' ? 'Research' : 'Finding people'} {job.status === 'running' ? 'running…' : job.status === 'done' ? 'done' : 'failed'} · started {when(job.createdAt)}
      {job.createdBy ? ` by ${job.createdBy}` : ''}
      {job.status !== 'running' && ` · ${job.searches} searches · ${usdFine(job.costUsd)}`}
      {job.error && <div className="text-red-300">{job.error}</div>}
      {job.status === 'done' && job.kind === 'research' && job.result && (
        <span> · kept {String(job.result.kept)} findings{Number(job.result.dropped) ? `, dropped ${String(job.result.dropped)} that cited no page the search returned` : ''}</span>
      )}
      {job.status === 'done' && job.kind === 'find' && job.result && (
        <span>
          {' '}
          · added {String(job.result.added)}
          {Array.isArray(job.result.skipped) && job.result.skipped.length ? `, skipped ${job.result.skipped.length}` : ''}
        </span>
      )}
    </div>
  )
}

function Research({ persona, jobs, ai, canWrite, reload }: { persona: PersonaView & { researchEntries: ResearchView[] }; jobs: OutreachJob[]; ai: AiInfo | null; canWrite: boolean; reload: () => Promise<void> }): JSX.Element {
  const [focus, setFocus] = useState('')
  const [noteOpen, setNoteOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [sources, setSources] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const researchJobs = jobs.filter((j) => j.kind === 'research')
  const running = researchJobs.some((j) => j.status === 'running')

  async function run(): Promise<void> {
    setErr(null)
    const r = await startPersonaResearch(persona.id, focus)
    if (!r.ok) return setErr(r.error)
    setFocus('')
    await reload()
  }
  async function addNote(): Promise<void> {
    setErr(null)
    const r = await addPersonaNote(persona.id, { title, text, sources: sources.split(/\s+/).filter(Boolean) })
    if (!r.ok) return setErr(r.error)
    setTitle('')
    setText('')
    setSources('')
    setNoteOpen(false)
    await reload()
  }
  async function removeEntry(rid: string): Promise<void> {
    const r = await deletePersonaResearch(rid)
    if (r.ok) await reload()
  }

  return (
    <Card title="The market" testId="persona-research">
      {canWrite && (
        <div className="space-y-2 mb-4">
          <div className="flex flex-wrap gap-2">
            <input className={`${inputCls} flex-1 min-w-[14rem]`} placeholder="Optional focus, e.g. where they gather online" value={focus} onChange={(e) => setFocus(e.target.value)} data-testid="research-focus" />
            <button className="btn-primary" disabled={running || !ai?.available} onClick={() => void run()} data-testid="research-run">
              {running ? 'Researching…' : 'Research with AI'}
            </button>
            <button className="btn-ghost" onClick={() => setNoteOpen(!noteOpen)} data-testid="research-note-toggle">
              Add your own note
            </button>
          </div>
          <div className="text-[11px] text-slate-500">
            {ai?.available
              ? `Runs ${ai.model} with web search on Plexii's key. A run usually costs well under a dollar; the exact cost shows when it finishes. Only findings that cite a page the search actually returned are kept.`
              : 'AI research is off: the server has no Anthropic key.'}
          </div>
          {noteOpen && (
            <div className="rounded-lg bg-white/[0.03] p-3 space-y-2" data-testid="research-note-form">
              <input className={inputCls} placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
              <textarea className={inputCls} rows={3} placeholder="What you learned" value={text} onChange={(e) => setText(e.target.value)} />
              <input className={inputCls} placeholder="Links to sources, separated by spaces" value={sources} onChange={(e) => setSources(e.target.value)} />
              <button className="btn-primary" disabled={!title.trim() || !text.trim()} onClick={() => void addNote()}>
                Save note
              </button>
            </div>
          )}
          {err && <div className="text-xs text-red-300" data-testid="research-error">{err}</div>}
        </div>
      )}
      {researchJobs.slice(0, 3).map((j) => (
        <JobLine key={j.id} job={j} />
      ))}
      {persona.researchEntries.length === 0 ? (
        <div className="text-sm text-slate-500 mt-2">No research yet.</div>
      ) : (
        <div className="space-y-5 mt-3">
          {persona.researchEntries.map((r) => (
            <div key={r.id} data-testid={`research-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">
                  {r.title} <Pill tone={r.kind === 'ai' ? 'accent' : 'muted'}>{r.kind === 'ai' ? 'AI, check the sources' : 'Your note'}</Pill>
                </div>
                {canWrite && (
                  <button className="text-[11px] text-slate-500 hover:text-red-300" onClick={() => void removeEntry(r.id)}>
                    delete
                  </button>
                )}
              </div>
              <div className="text-[11px] text-slate-500">
                {when(r.createdAt)}
                {r.createdBy ? ` · ${r.createdBy}` : ''}
                {r.body.dropped ? ` · ${r.body.dropped} uncited findings were dropped` : ''}
              </div>
              {r.body.summary && <p className="text-sm text-slate-300 mt-2">{r.body.summary}</p>}
              {r.body.sections.map((s, i) => (
                <div key={i} className="mt-3">
                  <div className="text-xs text-slate-400 uppercase tracking-wider">{s.heading}</div>
                  <ul className="mt-1 space-y-1.5">
                    {s.points.map((pt, k) => (
                      <li key={k} className="text-sm text-slate-200">
                        {pt.text}{' '}
                        {pt.sources.map((src) => (
                          <a key={src.url} href={src.url} target="_blank" rel="noreferrer" className="text-[11px] text-accent hover:underline mr-2" title={src.title ?? src.url}>
                            {shortUrl(src.url)} ↗
                          </a>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function FindPeople({
  persona,
  jobs,
  ai,
  canWrite,
  value,
  onCriteria,
  reload,
  onOpenPerson,
  people
}: {
  persona: PersonaView
  jobs: OutreachJob[]
  ai: AiInfo | null
  canWrite: boolean
  value: string
  onCriteria: (v: string) => void
  reload: () => Promise<void>
  onOpenPerson: (id: string) => void
  people: RemoteProspect[]
}): JSX.Element {
  const [count, setCount] = useState(8)
  const [err, setErr] = useState<string | null>(null)
  const findJobs = jobs.filter((j) => j.kind === 'find')
  const running = findJobs.some((j) => j.status === 'running')
  const lastDone = findJobs.find((j) => j.status === 'done')
  const added = Array.isArray(lastDone?.result?.prospectIds) ? (lastDone!.result!.prospectIds as string[]) : []

  async function run(): Promise<void> {
    setErr(null)
    const r = await startFindPeople(persona.id, count, value)
    if (!r.ok) return setErr(r.error)
    await reload()
  }

  return (
    <Card title="Find people like this" testId="persona-find">
      <label className="block">
        <div className="text-xs text-slate-400 mb-1">Who to look for (saved with the persona)</div>
        <textarea className={inputCls} rows={2} value={value} readOnly={!canWrite} onChange={(e) => onCriteria(e.target.value)} placeholder="e.g. publishes a weekly newsletter, US-based, runs a small agency" data-testid="persona-field-findCriteria" />
      </label>
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <label className="text-xs text-slate-400 flex items-center gap-1.5">
            How many
            <input type="number" min={1} max={ai?.findMax ?? 15} value={count} onChange={(e) => setCount(Number(e.target.value))} className="w-16 bg-black/30 border border-white/10 rounded px-2 py-1 text-sm" data-testid="find-count" />
          </label>
          <button className="btn-primary" disabled={running || !ai?.available} onClick={() => void run()} data-testid="find-run">
            {running ? 'Searching…' : 'Find people with AI'}
          </button>
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-1">
        Each person comes with the public pages that show what they do. They arrive unconfirmed and unchecked, with no messages written: check who they are first. A LinkedIn address is kept only if the search itself returned it.
      </div>
      {err && <div className="text-xs text-red-300 mt-1" data-testid="find-error">{err}</div>}
      <div className="mt-2 space-y-1">
        {findJobs.slice(0, 3).map((j) => (
          <JobLine key={j.id} job={j} />
        ))}
      </div>
      {added.length > 0 && (
        <ul className="mt-2 text-sm space-y-0.5">
          {added.map((pid) => {
            const p = people.find((x) => x.id === pid)
            return p ? (
              <li key={pid}>
                <button className="hover:text-accent" onClick={() => onOpenPerson(pid)}>
                  {p.name}
                </button>
                <span className="text-slate-500"> · {[p.role, p.company].filter(Boolean).join(', ')}</span>
              </li>
            ) : null
          })}
        </ul>
      )}
    </Card>
  )
}
